import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Form, Input, Button, Typography, message, Select, Progress } from 'antd';
import {
    UserOutlined, PhoneOutlined, MailOutlined,
    CheckCircleOutlined, ArrowLeftOutlined, TeamOutlined, LockOutlined,
    EyeInvisibleOutlined, EyeTwoTone, ArrowRightOutlined,
    DollarOutlined, RocketOutlined, GiftOutlined, SafetyCertificateOutlined
} from '@ant-design/icons';
import { affiliateService, cityService } from '../../common/services/api';
import './AffiliateRegister.css';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const AffiliateRegisterPage = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [cities, setCities] = useState([]);

    useEffect(() => {
        const loadCities = async () => {
            try {
                const response = await cityService.getActiveCities();
                if (response.success) {
                    setCities(response.data || []);
                }
            } catch (error) {
                console.error('Erreur chargement villes:', error);
            }
        };
        loadCities();
    }, []);

    const totalSteps = 3;
    const progressPercent = ((currentStep + 1) / totalSteps) * 100;

    const stepFields = {
        0: ['firstName', 'lastName'],
        1: ['phone', 'password', 'confirmPassword'],
        2: ['city', 'address', 'howDidYouHear', 'motivation']
    };

    const stepTitles = [
        { title: 'Identité', subtitle: 'Vos informations personnelles' },
        { title: 'Contact & Sécurité', subtitle: 'Vos coordonnées et mot de passe' },
        { title: 'Profil', subtitle: 'Quelques informations supplémentaires' }
    ];

    const validateStep = async () => {
        try {
            await form.validateFields(stepFields[currentStep]);
            return true;
        } catch (error) {
            return false;
        }
    };

    const nextStep = async () => {
        const isValid = await validateStep();
        if (isValid && currentStep < totalSteps - 1) {
            setCurrentStep(currentStep + 1);
        }
    };

    const prevStep = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const onFinish = async (values) => {
        setLoading(true);
        try {
            // Convert names to uppercase
            const formData = {
                ...values,
                firstName: values.firstName?.toUpperCase(),
                lastName: values.lastName?.toUpperCase()
            };
            const response = await affiliateService.submitApplication(formData);
            if (response.success) {
                setSubmitted(true);
            } else {
                message.error(response.message || 'Erreur lors de la soumission');
            }
        } catch (error) {
            message.error(error.message || 'Erreur lors de la soumission');
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <div className="register-page">
                <div className="register-success">
                    <div className="success-content">
                        <div className="success-icon-wrapper">
                            <CheckCircleOutlined />
                        </div>
                        <Title level={2}>Candidature envoyée !</Title>
                        <Paragraph className="success-text">
                            Nous avons bien reçu votre candidature. Notre équipe l'examinera
                            dans les <strong>24 à 48 heures</strong> et vous contactera.
                        </Paragraph>
                        <div className="success-timeline">
                            <div className="timeline-item completed">
                                <div className="timeline-dot"><CheckCircleOutlined /></div>
                                <span>Candidature reçue</span>
                            </div>
                            <div className="timeline-item">
                                <div className="timeline-dot">2</div>
                                <span>Examen en cours</span>
                            </div>
                            <div className="timeline-item">
                                <div className="timeline-dot">3</div>
                                <span>Validation</span>
                            </div>
                        </div>
                        <div className="success-buttons">
                            <Button type="primary" size="large" onClick={() => navigate('/')}>
                                Retour à l'accueil
                            </Button>
                            <Button size="large" onClick={() => navigate('/affilies/connexion')}>
                                Se connecter
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="register-page">
            {/* Left Panel - Branding */}
            <div className="register-branding">
                <div className="branding-content">
                    <Link to="/affilies" className="back-link">
                        <ArrowLeftOutlined /> Retour
                    </Link>

                    <div className="branding-header">
                        <Title level={1}>Rejoignez le réseau d'affiliés TIGOON</Title>
                        <Paragraph>
                            Développez vos revenus en recrutant des chauffeurs partenaires.
                        </Paragraph>
                    </div>

                    <div className="benefits-list">
                        <div className="benefit-item">
                            <div className="benefit-icon"><DollarOutlined /></div>
                            <div className="benefit-content">
                                <Text strong>Commissions attractives</Text>
                                <Text>Gagnez sur chaque chauffeur recruté</Text>
                            </div>
                        </div>
                        <div className="benefit-item">
                            <div className="benefit-icon"><RocketOutlined /></div>
                            <div className="benefit-content">
                                <Text strong>Validation rapide</Text>
                                <Text>Réponse sous 24-48h</Text>
                            </div>
                        </div>
                        <div className="benefit-item">
                            <div className="benefit-icon"><TeamOutlined /></div>
                            <div className="benefit-content">
                                <Text strong>Support dédié</Text>
                                <Text>Une équipe à votre écoute</Text>
                            </div>
                        </div>
                        <div className="benefit-item">
                            <div className="benefit-icon"><GiftOutlined /></div>
                            <div className="benefit-content">
                                <Text strong>Bonus exclusifs</Text>
                                <Text>Récompenses sur objectifs</Text>
                            </div>
                        </div>
                    </div>

                    <div className="branding-footer">
                        <SafetyCertificateOutlined />
                        <Text>Inscription 100% gratuite et sécurisée</Text>
                    </div>
                </div>
            </div>

            {/* Right Panel - Form */}
            <div className="register-form-panel">
                <div className="form-container">
                    <Link to="/affilies" className="form-back-link">
                        <ArrowLeftOutlined /> Retour au programme affiliés
                    </Link>

                    <div className="form-progress">
                        <div className="progress-info">
                            <Text strong>Étape {currentStep + 1} sur {totalSteps}</Text>
                            <Text type="secondary">{stepTitles[currentStep].title}</Text>
                        </div>
                        <Progress
                            percent={progressPercent}
                            showInfo={false}
                            strokeColor="#D4A853"
                            trailColor="#f0f0f0"
                        />
                    </div>

                    <div className="form-header">
                        <Title level={2}>{stepTitles[currentStep].title}</Title>
                        <Text type="secondary">{stepTitles[currentStep].subtitle}</Text>
                    </div>

                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={onFinish}
                        requiredMark={false}
                        className="affiliate-form"
                    >
                        {/* Step 1: Identity */}
                        <div className={`form-step ${currentStep === 0 ? 'active' : ''}`}>
                            <div className="form-grid">
                                <Form.Item
                                    name="firstName"
                                    label="Prénom"
                                    rules={[{ required: true, message: 'Requis' }]}
                                >
                                    <Input
                                        placeholder="Votre prénom"
                                        size="large"
                                        prefix={<UserOutlined className="field-icon" />}
                                        className="input-uppercase"
                                    />
                                </Form.Item>
                                <Form.Item
                                    name="lastName"
                                    label="Nom"
                                    rules={[{ required: true, message: 'Requis' }]}
                                >
                                    <Input
                                        placeholder="Votre nom"
                                        size="large"
                                        prefix={<UserOutlined className="field-icon" />}
                                        className="input-uppercase"
                                    />
                                </Form.Item>
                            </div>
                        </div>

                        {/* Step 2: Contact & Security */}
                        <div className={`form-step ${currentStep === 1 ? 'active' : ''}`}>
                            <div className="form-grid">
                                <Form.Item
                                    name="phone"
                                    label="Téléphone"
                                    rules={[
                                        { required: true, message: 'Requis' },
                                        { pattern: /^[0-9+]{8,15}$/, message: 'Numéro invalide' }
                                    ]}
                                >
                                    <Input
                                        placeholder="07 XX XX XX XX"
                                        size="large"
                                        prefix={<PhoneOutlined className="field-icon" />}
                                        onChange={(e) => {
                                            const cleanValue = e.target.value.replace(/\s/g, '');
                                            form.setFieldValue('phone', cleanValue);
                                        }}
                                    />
                                </Form.Item>
                                <Form.Item
                                    name="email"
                                    label="Email (optionnel)"
                                    rules={[
                                        { type: 'email', message: 'Email invalide' }
                                    ]}
                                >
                                    <Input
                                        placeholder="votre@email.com"
                                        size="large"
                                        prefix={<MailOutlined className="field-icon" />}
                                    />
                                </Form.Item>
                            </div>
                            <div className="form-grid">
                                <Form.Item
                                    name="password"
                                    label="Mot de passe"
                                    rules={[{ required: true, message: 'Requis' }]}
                                >
                                    <Input.Password
                                        placeholder="Votre mot de passe"
                                        size="large"
                                        prefix={<LockOutlined className="field-icon" />}
                                        iconRender={(visible) => visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
                                    />
                                </Form.Item>
                                <Form.Item
                                    name="confirmPassword"
                                    label="Confirmer le mot de passe"
                                    dependencies={['password']}
                                    rules={[
                                        { required: true, message: 'Requis' },
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (!value || getFieldValue('password') === value) {
                                                    return Promise.resolve();
                                                }
                                                return Promise.reject(new Error('Les mots de passe ne correspondent pas'));
                                            },
                                        }),
                                    ]}
                                >
                                    <Input.Password
                                        placeholder="Confirmez votre mot de passe"
                                        size="large"
                                        prefix={<LockOutlined className="field-icon" />}
                                        iconRender={(visible) => visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
                                    />
                                </Form.Item>
                            </div>
                        </div>

                        {/* Step 3: Profile */}
                        <div className={`form-step ${currentStep === 2 ? 'active' : ''}`}>
                            <div className="form-grid">
                                <Form.Item
                                    name="city"
                                    label="Ville"
                                    rules={[{ required: true, message: 'Requis' }]}
                                >
                                    <Select placeholder="Sélectionnez" size="large" showSearch optionFilterProp="children">
                                        {cities.map(city => (
                                            <Option key={city._id} value={city._id}>{city.name}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                                <Form.Item name="address" label="Quartier">
                                    <Input placeholder="Votre quartier" size="large" />
                                </Form.Item>
                            </div>
                            <Form.Item name="howDidYouHear" label="Comment avez-vous connu TIGOON ?">
                                <Select placeholder="Sélectionnez" size="large">
                                    <Option value="social">Réseaux sociaux</Option>
                                    <Option value="friend">Ami / Connaissance</Option>
                                    <Option value="driver">Chauffeur TIGOON</Option>
                                    <Option value="ad">Publicité</Option>
                                    <Option value="other">Autre</Option>
                                </Select>
                            </Form.Item>
                            <Form.Item name="motivation" label="Motivation (optionnel)">
                                <TextArea
                                    rows={3}
                                    placeholder="Pourquoi souhaitez-vous devenir affilié ?"
                                    maxLength={300}
                                    showCount
                                />
                            </Form.Item>
                        </div>

                        {/* Navigation */}
                        <div className="form-navigation">
                            {currentStep > 0 && (
                                <Button
                                    size="large"
                                    onClick={prevStep}
                                    className="btn-prev"
                                >
                                    <ArrowLeftOutlined /> Retour
                                </Button>
                            )}

                            {currentStep < totalSteps - 1 ? (
                                <Button
                                    type="primary"
                                    size="large"
                                    onClick={nextStep}
                                    className="btn-next"
                                >
                                    Continuer <ArrowRightOutlined />
                                </Button>
                            ) : (
                                <Button
                                    type="primary"
                                    size="large"
                                    htmlType="submit"
                                    loading={loading}
                                    className="btn-submit"
                                >
                                    {loading ? 'Envoi...' : 'Soumettre ma candidature'}
                                </Button>
                            )}
                        </div>
                    </Form>

                    <div className="form-footer">
                        <Text type="secondary">
                            Déjà inscrit ? <Link to="/affilies/connexion">Se connecter</Link>
                        </Text>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AffiliateRegisterPage;
